import React, { useEffect, useState } from 'react'
import { Button, WhatsAppButton } from '@olxbr/ds-core'
import { Container, Alertbox } from '@olxbr/ds-web'
import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'
import Lock from '@olxbr/olx-icons/dist/System/Lock'

import {
  isValid as isValidPhone,
  removeMask as removePhoneMask,
  isLandline,
} from '../../../util/PhonePattern'
import { makeCall, makeCallv4 } from '../../../api/phoneValidationApiCall'
import { sendPinCode as sendPinCodeV4 } from '../../../api/pinCodeApi'
import Lurker from '../../../util/lurker'
import { MessageCircle } from '@olxbr/ds-icons'
import { SEND_MODES } from '../CheckPinCodeTemplate'

import { PhoneInput } from '../../atoms/PhoneInput/PhoneInput'
import { ErrorAlertbox } from '../../atoms/ErrorAlertbox/ErrorAlertbox'
import { formatedTime } from '../../../util/date'
import { ConfirmPhoneNumberModal } from '../ConfirmPhoneNumberModal/ConfirmPhoneNumberModal'
import { breakpoints } from '../../../util/medias'
import { View } from '../../atoms/View'

const getContentElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Container {...props} />

const ButtonsWrapper = styled.div`
  margin-top: var(--spacing-3);
  display: flex;
  justify-content: end;
  gap: var(--spacing-2);

  & > * {
    flex: 1;
  }

  @media screen and (max-width: ${breakpoints['mobile']}px) {
    justify-content: center;
    flex-direction: column-reverse;
    align-items: stretch;
    flex: 1;

    & > * {
      width: 100%;
    }
  }
`

const Form = styled.form``

const ContainerWrapper = styled(getContentElement)`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    border: none;
    padding: var(--spacing-0-25);
    width: 100% !important;
    max-width: 100% !important;
    display: block !important;
  }
`

const StyledButton = styled(Button)`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    margin: 0;
    width: 100%;
  }
`

const Icon = styled.span`
  margin-right: var(--spacing-1);
`

const StyledText = styled.span.attrs({ className: 'typo-body-medium' })`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    font-size: var(--font-size-1-75);
  }
`

const AlertboxesWrapper = styled.div`
  margin-top: var(--spacing-3);
  display: none;
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    display: block;
  }
`

const AlertboxesInline = styled.div`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    display: none;
  }
`

const lurker = new Lurker('phone_input')

export const SendPinCodeTemplate = ({
  onClickBounce,
  onSuccess,
  onPinCodeSent,
  onAlreadyHasCodeClick,
  lockTime,
  showBounceLink,
  abTests,
  token,
  modalMode,
  onChooseSendMode,
}) => {
  const { control, handleSubmit, formState, getValues, clearErrors } = useForm({
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: { phone: '' },
  })

  const [errors, setErrors] = useState({
    showGenericError: false,
    showRateLimitError: false,
    showMaxPhoneValidationError: false,
    showPhoneInUseError: false,
    showPhoneInBlackListError: false,
    showSfaBlockError: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [sendModeSelected, setSendModeSelected] = useState(SEND_MODES.PHONE)
  const [sfaErrorCode, setSfaErrorCode] = useState('')

  const onSendCodeClick = (e, sendMode) => {
    e.preventDefault()

    const eventType =
      sendMode === SEND_MODES.WHATSAPP
        ? 'open_modal_from_whatsapp_button'
        : 'open_modal_from_sms_button'
    lurker.clickEvt(eventType, {
      type: sendMode,
    })

    if (!modalMode) {
      setSendModeSelected(sendMode)
      setModalOpen(true)
    } else {
      handleSubmit(data => onSubmit(data, sendMode))()
    }
  }

  const isLocked = lockTime > 0
  const sfaDisplayCode = sfaErrorCode.split('_').slice(1).join('_')

  const showError = error => {
    setErrors(errors => ({ ...errors, [error]: true }))
    setIsSubmitting(false)
  }

  const onValidatePhoneSuccess = (phone, opts = {}) => {
    lurker.viewMessageEvt('success', opts)
    if (onPinCodeSent) {
      onPinCodeSent()
    }
    setIsSubmitting(false)
    if (onSuccess) {
      onSuccess(phone)
    }
  }

  const closeModal = e => {
    if (e) e.preventDefault()
    setModalOpen(false)
  }

  const onValidatePhoneError = (error = {}, opts = {}) => {
    const { response: { status, data: { message } = {} } = {} } = error

    if (status === 401) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'unauthorized',
        accounts_status_code: status,
        accounts_error_message: message || 'unknown',
        ...opts,
      })
      showError('showGenericError')
    } else if (status === 429) {
      if (message === 'MAX_PHONE_VALIDATION_ATTEMPTS_REACHED') {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'max_phone_validation',
          accounts_status_code: status,
        })
        showError('showMaxPhoneValidationError')
      } else {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'rate_limit_exceeded',
          accounts_status_code: status,
          accounts_error_message: message || 'unknown',
          ...opts,
        })
        showError('showGenericError')
      }
    } else if (status === 403) {
      switch (message) {
        case 'PHONE_IN_BLACKLIST':
          lurker.viewMessageEvt('error', {
            accounts_reason: 'phone_number_in_blacklist',
            accounts_status_code: status,
            ...opts,
          })
          showError('showPhoneInBlackListError')
          break
        case 'PHONE_IN_USE':
          lurker.viewMessageEvt('error', {
            accounts_reason: 'phone_number_already_in_use',
            accounts_status_code: status,
            ...opts,
          })
          showError('showPhoneInUseError')
          break
        case 'ACCOUNT_LOCKED':
          lurker.viewMessageEvt('error', {
            accounts_reason: message.toLowerCase(),
            accounts_status_code: status,
            ...opts,
          })

          showError('showRateLimitError')
          break
        case 'ERROR_KOHQXEDC9G':
          lurker.viewMessageEvt('error', {
            accounts_reason: message.toLowerCase(),
            accounts_status_code: status,
            ...opts,
          })
          setSfaErrorCode(message)
          showError('showSfaBlockError')
          break
        default:
          lurker.viewMessageEvt('error', {
            accounts_reason: 'unexpected_status_code',
            accounts_status_code: status,
            accounts_error_message: message || 'unknown',
            ...opts,
          })

          showError('showGenericError')
      }
    } else if (status) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'unexpected_status_code',
        accounts_status_code: status,
        accounts_error_message: message || 'unknown',
        ...opts,
      })
      showError('showGenericError')
    } else {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'unexpected_error',
        accounts_error_message: message || 'unknown',
        ...opts,
      })

      showError('showGenericError')
    }
  }

  const phoneVerifyClick = event => {
    if (event) event.preventDefault()
    lurker.clickEvt('phone_already_received_pincode_click', {
      flow: 'phone_validation',
      type: 'sms',
    })
    onAlreadyHasCodeClick(getValues('phone'))
  }

  const onSubmit = (data, sendModeOverride) => {
    const phone = removePhoneMask(data.phone)
    const landLine = isLandline(phone)
    const isWhatsapp = sendModeOverride === SEND_MODES.WHATSAPP
    const type = isWhatsapp ? 'whatsapp' : 'sms'
    const trackingOpts = {
      landLine,
      phone,
      flow: 'phone_validation',
      ...{ type },
    }

    lurker.clickEvt('submit', trackingOpts)
    setIsSubmitting(true)

    if (onChooseSendMode) onChooseSendMode(sendModeOverride || SEND_MODES.PHONE)

    let actionPromise

    const makePhoneCall = token ? phone => makeCallv4(phone, token) : makeCall
    actionPromise = landLine ? makePhoneCall(phone) : sendPinCodeV4(phone, sendModeOverride, token)

    actionPromise
      .then(() => {
        onValidatePhoneSuccess(phone, trackingOpts)
      })
      .catch(error => {
        onValidatePhoneError(error, trackingOpts)
      })
      .finally(() => {
        !modalMode && closeModal()
      })
  }

  useEffect(() => {
    const loginMethod = globalThis.loginMethod || 'unknown'
    lurker.pageLoadEvt('phone_input', {
      flow: 'phone_validation',
      type: 'sms',
      sent_from: loginMethod,
    })
  }, [])

  return (
    <Form>
      <div className="flex flex-col">
        <ContainerWrapper
          modalMode={modalMode}
          type="outlined"
          paddingBottom="--spacing-8"
          paddingTop="--spacing-8"
          paddingLeft="--spacing-8"
          paddingRight="--spacing-8"
        >
          <div className="flex flex-col">
            {errors.showGenericError && (
              <ErrorAlertbox title="Falha em nosso sistema" showContactUs={false}>
                Estamos trabalhando pra resolver rapidinho. Tente novamente em alguns minutos.
              </ErrorAlertbox>
            )}
            {errors.showPhoneInBlackListError && (
              <ErrorAlertbox>Este número não pode ser cadastrado novamente.</ErrorAlertbox>
            )}
            {errors.showPhoneInUseError && (
              <ErrorAlertbox showContactUs={false} title="Limite diário atingido para este número">
                Tente novamente daqui {formatedTime(lockTime)}, ou use outro número de telefone.
              </ErrorAlertbox>
            )}
            {errors.showMaxPhoneValidationError && (
              <ErrorAlertbox showContactUs={false} title="Limite de verificações atingido">
                Esse telefone já foi utilizado para verificar uma conta nos últimos 30 dias.
              </ErrorAlertbox>
            )}
            {errors.showSfaBlockError && (
              <ErrorAlertbox title={`CodeError: ${sfaDisplayCode}`} showContactUs>
                Por favor, entre em contato antes de tentar novamente!
              </ErrorAlertbox>
            )}
            {errors.showRateLimitError && (
              <ErrorAlertbox title="Telefone temporariamente bloqueado" showContactUs />
            )}
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: phone => isValidPhone(removePhoneMask(phone)),
              }}
              render={({ field, fieldState }) => (
                <PhoneInput
                  {...field}
                  {...fieldState}
                  clearErrors={() => clearErrors('phone')}
                  fullwidth
                />
              )}
            />
            {isLocked && (
              <div className="flex justify-start">
                <Button variant="link" className="mt-1" size="small" onClick={phoneVerifyClick}>
                  Já tem um código?
                </Button>
              </div>
            )}
            <AlertboxesInline>
              <div className="flex mb-1 mt-3">
                <Alertbox>
                  <StyledText className="font-semibold">
                    O mesmo número de telefone não pode ser usado em mais de uma conta OLX.
                  </StyledText>
                </Alertbox>
              </div>
              <Alertbox icon={<Lock />}>
                <StyledText className="font-semibold">
                  Seu número não será exibido para outros usuários.
                </StyledText>
              </Alertbox>
            </AlertboxesInline>
          </div>
        </ContainerWrapper>
        <ButtonsWrapper>
          <>
            <StyledButton
              size="large"
              disabled={!formState.isValid || isLocked || isSubmitting}
              type="button"
              role="button"
              variant="tertiary"
              aria-label="Enviar código por SMS"
              onClick={e => onSendCodeClick(e, SEND_MODES.PHONE)}
            >
              <Icon>
                <MessageCircle size="24" />
              </Icon>
              Receber por SMS
            </StyledButton>
            <WhatsAppButton
              size="large"
              disabled={!formState.isValid || isLocked || isSubmitting}
              type="button"
              role="button"
              aria-label="Enviar código por WhatsApp"
              onClick={e => onSendCodeClick(e, SEND_MODES.WHATSAPP)}
            >
              Receber por WhatsApp
            </WhatsAppButton>
          </>
        </ButtonsWrapper>
        <AlertboxesWrapper>
          <div className="flex mb-1 mt-3">
            <Alertbox>
              <StyledText className="font-semibold">
                O mesmo número de telefone não pode ser usado em mais de uma conta OLX.
              </StyledText>
            </Alertbox>
          </div>
          <Alertbox icon={<Lock />}>
            <StyledText className="font-semibold">
              Seu número não será exibido para outros usuários.
            </StyledText>
          </Alertbox>
        </AlertboxesWrapper>
      </div>
      <ConfirmPhoneNumberModal
        phone={getValues('phone')}
        closeModal={closeModal}
        isSubmitting={isSubmitting}
        isOpen={modalOpen}
        onConfirm={handleSubmit(data => onSubmit(data, sendModeSelected))}
      />
    </Form>
  )
}
